import React, { Suspense, useMemo } from 'react';
import { useParams } from 'react-router';
import classNames from 'classnames';
import Slider from 'react-slick';

import {
	VideosLayoutTypesBold,
	VideosLayoutTypesClassic,
	VideosLayoutTypesFun,
} from '../../../../types/template-layouts';
import { ContentEditors, HomepageVideo, ModuleMap, PageModule, PageModuleType } from '../../../../types/working-model';
import { EIcon } from '../../../general-ui/icon/icon';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { getStylingOverrides } from '../../utils';
import { ParamsProps } from '../../live-event';
import { getDefaultModuleAlignment, getTextValue } from '../../../../utils/utils';
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import '../../../../scss/live-event/base/modules/videos.scss';
import { useTranslate } from '../../../../i18n/useTranslationModules';
import useTranslationRoute from '../../hooks/use-translation-route';
const Video = React.lazy(() => import('./video'));

interface VideoProps {
	module: PageModule;
	template: string;
	isLandingPage?: boolean;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function PrevArrow(props: any): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<EIcon name="keyboard-arrow-left" size="giant" />
		</div>
	);
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function NextArrow(props: any): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<EIcon name="keyboard-arrow-right" size="giant" />
		</div>
	);
}

const SHARED_SLIDER_SETTINGS = {
	infinite: true,
	speed: 500,
	slidesToShow: 1,
	slidesToScroll: 1,
};

export const SINGLE_SLIDER_SETTINGS = {
	...SHARED_SLIDER_SETTINGS,
	dots: true,
};

export const MULTI_SLIDER_SETTINGS = {
	...SHARED_SLIDER_SETTINGS,
	dots: false,
	centerMode: false,
	variableWidth: true,
	arrows: false,
};

const Videos = React.forwardRef<HTMLDivElement, VideoProps>(({ module, template }, ref) => {
	const { language }: ParamsProps = useParams();
	const { content, styling_overrides, modules } = module;
	const layout_type = content?.layout_type || VideosLayoutTypesClassic.standard;
	const {
		backgroundColor,
		backgroundImg,
		customHeadingOverrides,
		titleOverrides,
		descriptionOverrides,
	} = getStylingOverrides(styling_overrides);
	const translationRoute = useTranslationRoute();
	const { t } = useTranslate(translationRoute);

	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;

	const sliderSettings = useMemo(() => {
		const isVideoListPreview =
			layout_type === VideosLayoutTypesBold.ltr ||
			layout_type === VideosLayoutTypesClassic.ltr ||
			layout_type === VideosLayoutTypesFun.ltr;

		return isVideoListPreview
			? { ...MULTI_SLIDER_SETTINGS }
			: { ...SINGLE_SLIDER_SETTINGS };
	}, [layout_type]);

	const videos = modules;

	const { custom_heading, description, title } = getTextValue(content, language);

	return module.is_on && content && (
		<section
			id={ModuleMap[PageModuleType.videos]}
			className={classNames(
				'videos',
				`videos-${layout_type}`,
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<div className="video-title-container">
					<TypographyItem className={classNames('evt-caption-1 video-custom-heading', customHeadingOverrides)} tagName="p">
						{t(`videos.${module.id}.heading`, t('videos.heading', custom_heading))}
					</TypographyItem>
					<TypographyItem className={classNames('evt-heading-2 video-title', titleOverrides)} tagName="h2">
						{t(`videos.${module.id}.title`, t('videos.title', title))}
					</TypographyItem>
					<TypographyItem className={classNames('evt-body-text-1 video-description', titleOverrides, {
						'quill-editor': contentEditor === ContentEditors.quill
					})} tagName="div">
						<RenderEditorDescription
							description={t(`videos.${module.id}.description`, t('videos.description', description))}
							descriptionOverrides={classNames('video-description', descriptionOverrides)}
							useNew={contentEditor === ContentEditors.quill}
							defaultTextAlignment={getDefaultModuleAlignment(module, template)}
						/>
					</TypographyItem>
				</div>
				{/* <img
							src={content.image || 'https://brandlive-upload.s3-us-west-2.amazonaws.com/uploads/17/6a32749f-4028-4905-adf4-34c120d428c1/Windmill.jpg'}
							alt=""
							width="375"
							height="300"
							aria-hidden="true"
					/> */}
				{/* <div className="video-slider">{renderVideo()}</div> */}

				{videos && videos.length ? (
					<div className="video-slider">
						<Suspense fallback="">
							<Slider
								{...sliderSettings}
								prevArrow={<PrevArrow />}
								nextArrow={<NextArrow />}
							>
								{videos.map((video: HomepageVideo) => (
									<Video
										key={video.source}
										// hard code use_thumbnail so video thumbnail is consistent with what was displayed in editor
										video={{ ...video, use_thumbnail: true }}
										multi={videos.length > 1}
										template={template}
										isMain={false}
									/>
								))}
							</Slider>
						</Suspense>
					</div>
				) : null}
			</div>
		</section>
	);
});

Videos.displayName = 'Videos';

export default Videos;
